import React, { Children } from 'react';
import { ArrowLeft, BookOpen, Sparkles } from 'lucide-react';
import { BookConfiguration } from './StorybookSetupPage';
type ReviewConfirmPageProps = {
  storyId: number;
  config: BookConfiguration;
  onBack: () => void;
  onCreate: () => void;
};
export function ReviewConfirmPage({
  storyId,
  config,
  onBack,
  onCreate
}: ReviewConfirmPageProps) {
  // Mock story data
  const story = {
    id: storyId,
    thumbnail: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=400&h=400&fit=crop',
    title: 'greek mythos',
    date: 'December 2, 2025',
    author: 'Daniel Lowe',
    summary: 'It sounds like you told a fascinating story about Achilles, a man destined for heroism. You painted a picture of a time when gods and mortals were more intertwined, and how his mother, Thetis, a sea goddess, played a significant role in...'
  };
  const readingLevelLabels = {
    toddler: 'Toddler',
    adolescent: 'Adolescent',
    preteen: 'Pre-teen',
    teen: 'Teen'
  };
  const bookStyleLabels = {
    standard: 'Standard',
    photo: 'Photo Book',
    classic: 'Classic',
    modern: 'Modern',
    childrens: "Children's"
  };
  const artStyleLabels = {
    anime: 'anime',
    watercolor: 'watercolor',
    ghibli: 'studio ghibli',
    oil: 'oil painting',
    digital: 'digital art',
    realistic: 'realistic'
  };
  return <div className="flex-1 bg-gray-100 min-h-screen" data-id="element-1573">
      <div className="px-8 py-6" data-id="element-1574">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6" data-id="element-1575">
          <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium" data-id="element-1576">
            <ArrowLeft className="w-5 h-5" data-id="element-1577" />
            Back
          </button>
          <h1 className="text-3xl font-bold text-gray-900" data-id="element-1578">Review & Confirm</h1>
        </div>

        <div className="flex gap-6" data-id="element-1579">
          {/* Main Content */}
          <div className="flex-1 space-y-6" data-id="element-1580">
            {/* Story Details */}
            <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-1581">
              <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-1582">
                Story Details
              </h3>
              <p className="text-gray-600 mb-6" data-id="element-1583">
                The story you're creating a book from
              </p>

              <div className="flex gap-4" data-id="element-1584">
                <img src={story.thumbnail} alt={story.title} className="w-24 h-24 rounded-xl object-cover flex-shrink-0" data-id="element-1585" />
                <div className="flex-1" data-id="element-1586">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1587">
                    {story.title}
                  </h4>
                  <p className="text-sm text-gray-600 mb-3" data-id="element-1588">
                    {story.date} • {story.author}
                  </p>
                  <p className="text-sm text-gray-700 line-clamp-3" data-id="element-1589">
                    {story.summary}
                  </p>
                </div>
              </div>
            </div>

            {/* Book Configuration */}
            <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-1590">
              <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-1591">
                Book Configuration
              </h3>
              <p className="text-gray-600 mb-6" data-id="element-1592">Your selected options</p>

              <div className="grid grid-cols-2 gap-x-12 gap-y-6" data-id="element-1593">
                <div data-id="element-1594">
                  <div className="text-sm text-gray-600 mb-1" data-id="element-1595">Art Style</div>
                  <div className="font-semibold text-gray-900" data-id="element-1596">
                    {artStyleLabels[config.artStyle]}
                  </div>
                </div>

                <div data-id="element-1597">
                  <div className="text-sm text-gray-600 mb-1" data-id="element-1598">Book Style</div>
                  <div className="font-semibold text-gray-900" data-id="element-1599">
                    {bookStyleLabels[config.bookStyle]}
                  </div>
                </div>

                <div data-id="element-1600">
                  <div className="text-sm text-gray-600 mb-1" data-id="element-1601">
                    Reading Level
                  </div>
                  <div className="font-semibold text-gray-900" data-id="element-1602">
                    {readingLevelLabels[config.readingLevel]}
                  </div>
                </div>

                <div data-id="element-1603">
                  <div className="text-sm text-gray-600 mb-1" data-id="element-1604">Page Count</div>
                  <div className="font-semibold text-gray-900" data-id="element-1605">
                    {config.pageCount} pages
                  </div>
                </div>

                <div data-id="element-1606">
                  <div className="text-sm text-gray-600 mb-1" data-id="element-1607">Image Model</div>
                  <div className="font-semibold text-gray-900" data-id="element-1608">auto</div>
                </div>

                <div data-id="element-1609">
                  <div className="text-sm text-gray-600 mb-1" data-id="element-1610">Options</div>
                  <div className="font-semibold text-gray-900" data-id="element-1611">None</div>
                </div>
              </div>
            </div>

            {/* What happens next */}
            <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-1612">
              <h3 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-1613">
                What happens next?
              </h3>
              <div className="space-y-4" data-id="element-1614">
                <div className="flex gap-4" data-id="element-1615">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1616">
                    <span className="text-green-600 font-semibold" data-id="element-1617">1</span>
                  </div>
                  <div data-id="element-1618">
                    <div className="font-medium text-gray-900 mb-1" data-id="element-1619">
                      Book Generation
                    </div>
                    <p className="text-sm text-gray-600" data-id="element-1620">
                      Our AI will transform your story into a beautifully
                      illustrated book with your chosen style and settings.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4" data-id="element-1621">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1622">
                    <span className="text-green-600 font-semibold" data-id="element-1623">2</span>
                  </div>
                  <div data-id="element-1624">
                    <div className="font-medium text-gray-900 mb-1" data-id="element-1625">
                      Review & Edit
                    </div>
                    <p className="text-sm text-gray-600" data-id="element-1626">
                      Once generated, you'll be able to review each page, make
                      edits, and regenerate any pages you'd like to improve.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4" data-id="element-1627">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1628">
                    <span className="text-green-600 font-semibold" data-id="element-1629">3</span>
                  </div>
                  <div data-id="element-1630">
                    <div className="font-medium text-gray-900 mb-1" data-id="element-1631">
                      Share & Print
                    </div>
                    <p className="text-sm text-gray-600" data-id="element-1632">
                      When you're happy with your book, you can share it
                      digitally with family or order a physical printed copy.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Cost Summary Sidebar */}
          <div className="w-80" data-id="element-1633">
            <div className="bg-white rounded-2xl p-6 shadow-sm sticky top-6" data-id="element-1634">
              <div className="flex items-center gap-2 mb-6" data-id="element-1635">
                <BookOpen className="w-5 h-5 text-gray-700" data-id="element-1636" />
                <h3 className="text-xl font-semibold text-gray-900" data-id="element-1637">
                  Cost Summary
                </h3>
              </div>

              <div className="text-center mb-6 py-6 border-y border-gray-200" data-id="element-1638">
                <div className="text-sm text-gray-600 mb-2" data-id="element-1639">Total Cost</div>
                <div className="text-5xl font-bold text-green-600 mb-1" data-id="element-1640">5</div>
                <div className="text-lg text-gray-700" data-id="element-1641">credits</div>
              </div>

              <div className="flex items-center justify-between mb-6 text-gray-700" data-id="element-1642">
                <div className="flex items-center gap-2" data-id="element-1643">
                  <Sparkles className="w-4 h-4 text-green-600" data-id="element-1644" />
                  <span className="font-medium" data-id="element-1645">Available</span>
                </div>
                <span className="font-semibold" data-id="element-1646">35 / 50</span>
              </div>

              <button onClick={onCreate} className="w-full flex items-center justify-center gap-2 py-3 bg-[#4ade80] text-white font-semibold rounded-xl hover:bg-[#22c55e] transition-colors mb-3" data-id="element-1647">
                <BookOpen className="w-5 h-5" data-id="element-1648" />
                Create Book
              </button>

              <button onClick={onBack} className="w-full flex items-center justify-center gap-2 py-3 bg-white text-gray-700 font-semibold rounded-xl border-2 border-gray-300 hover:bg-gray-50 transition-colors" data-id="element-1649">
                <ArrowLeft className="w-5 h-5" data-id="element-1650" />
                Adjust Settings
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}